import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../lib/store.js';
import {
  IconActivity,
  IconBackup,
  IconBulk,
  IconDashboard,
  IconPlus,
  IconSearch,
  IconServer,
  IconSettings,
  IconUpload,
} from './Icons.js';

interface Item {
  id: string;
  category: 'Servers' | 'Machines' | 'Pages';
  label: string;
  hint?: string;
  to: string;
  icon: React.ReactNode;
  keywords: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Cmd+K palette. Keyboard-first, fuzzy substring matching, grouped by
 * category. Servers and machines are pulled from the live store; pages are
 * static. Top result is auto-selected and Enter submits.
 */
export default function CommandPalette({ open, onClose }: Props) {
  const navigate = useNavigate();
  const servers = useStore((s) => s.servers);
  const machines = useStore((s) => s.machines);
  const nodeStatus = useStore((s) => s.nodeStatus);
  const liveStats = useStore((s) => s.liveStats);
  const role = useStore((s) => s.user?.role);

  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Reset on open and focus the input
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const items = useMemo<Item[]>(() => {
    const all: Item[] = [];

    // Pages
    const pages: Array<{ label: string; to: string; icon: React.ReactNode; adminOnly?: boolean }> = [
      { label: 'Dashboard',   to: '/',           icon: <IconDashboard /> },
      { label: 'Backups',     to: '/backups',    icon: <IconBackup /> },
      { label: 'Files',       to: '/files',      icon: <IconUpload /> },
      { label: 'Bulk Actions', to: '/bulk',      icon: <IconBulk /> },
      { label: 'New Server',  to: '/servers/new', icon: <IconPlus /> },
      { label: 'Settings',    to: '/settings',   icon: <IconSettings /> },
    ];
    for (const p of pages) {
      if (p.label === 'Bulk Actions' && role === 'viewer') continue;
      if (p.label === 'New Server' && role === 'viewer') continue;
      all.push({
        id: `page:${p.to}`,
        category: 'Pages',
        label: p.label,
        to: p.to,
        icon: p.icon,
        keywords: p.label.toLowerCase(),
      });
    }

    // Machines
    for (const name of Array.from(machines.keys()).sort()) {
      const tel = machines.get(name);
      const online = !!nodeStatus.get(name)?.online;
      all.push({
        id: `machine:${name}`,
        category: 'Machines',
        label: name,
        hint: tel ? `${tel.cpu.load_pct.toFixed(0)}% CPU · ${online ? 'online' : 'offline'}` : (online ? 'online' : 'offline'),
        to: `/machines/${encodeURIComponent(name)}`,
        icon: <IconActivity />,
        keywords: name.toLowerCase(),
      });
    }

    // Also surface machines that only appear via servers (not yet in telemetry)
    const seenMachines = new Set(machines.keys());
    for (const s of servers.values()) {
      if (!seenMachines.has(s.Server_Machine)) {
        seenMachines.add(s.Server_Machine);
        all.push({
          id: `machine:${s.Server_Machine}`,
          category: 'Machines',
          label: s.Server_Machine,
          to: `/machines/${encodeURIComponent(s.Server_Machine)}`,
          icon: <IconActivity />,
          keywords: s.Server_Machine.toLowerCase(),
        });
      }
    }

    // Servers
    const sortedServers = Array.from(servers.values()).sort((a, b) => a.Server_Name.localeCompare(b.Server_Name));
    for (const s of sortedServers) {
      const live = liveStats.get(s.Server_ID);
      const players = live?.rcon ? `${live.rcon.Players ?? 0}/${live.rcon.MaxPlayers ?? '—'}` : '';
      all.push({
        id: `server:${s.Server_ID}`,
        category: 'Servers',
        label: s.Server_Name,
        hint: [s.Server_Machine, s.Server_Group, players].filter(Boolean).join(' · '),
        to: `/servers/${s.Server_ID}`,
        icon: <IconServer />,
        keywords: `${s.Server_Name} ${s.Server_Machine} ${s.Server_Group} ${s.Server_Org ?? ''} #${s.Server_ID}`.toLowerCase(),
      });
    }

    return all;
  }, [servers, machines, nodeStatus, liveStats, role]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items.slice(0, 30);
    return items
      .map((item) => ({ item, score: score(q, item.keywords) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 30)
      .map((x) => x.item);
  }, [query, items]);

  // Group while preserving the global sort order
  const grouped = useMemo(() => {
    const groups = new Map<Item['category'], Item[]>();
    for (const it of filtered) {
      const list = groups.get(it.category) ?? [];
      list.push(it);
      groups.set(it.category, list);
    }
    return Array.from(groups.entries());
  }, [filtered]);

  // Reset active index when results change
  useEffect(() => { setActiveIdx(0); }, [query]);

  // Scroll active item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${activeIdx}"]`) as HTMLElement | null;
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  if (!open) return null;

  const submit = (idx: number) => {
    const it = filtered[idx];
    if (!it) return;
    onClose();
    navigate(it.to);
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-bg/70 backdrop-blur-sm flex items-start justify-center p-4 pt-[12vh] animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
    >
      <div
        className="card w-full max-w-xl shadow-modal animate-slide-up overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 h-12 border-b border-bg-border">
          <span className="text-fg-dim"><IconSearch /></span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Jump to a server, machine, or page…"
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-fg-dim"
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActiveIdx((i) => Math.max(0, i - 1));
              } else if (e.key === 'Enter') {
                e.preventDefault();
                submit(activeIdx);
              } else if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
              }
            }}
          />
          <span className="kbd">esc</span>
        </div>

        <div ref={listRef} className="max-h-[50vh] overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-fg-dim">No matches</div>
          ) : (
            grouped.map(([category, list]) => (
              <div key={category} className="py-1">
                <div className="px-4 py-1 text-[10px] uppercase tracking-wider font-semibold text-fg-dim">
                  {category}
                </div>
                {list.map((it) => {
                  const idx = filtered.indexOf(it);
                  const active = idx === activeIdx;
                  return (
                    <button
                      key={it.id}
                      type="button"
                      data-idx={idx}
                      className={`w-full flex items-center gap-3 px-4 py-2 text-sm cursor-pointer transition-colors ${
                        active ? 'bg-bg-hover text-fg' : 'text-fg-muted hover:bg-bg-hover/60 hover:text-fg'
                      }`}
                      onMouseMove={() => setActiveIdx(idx)}
                      onClick={() => submit(idx)}
                    >
                      <span className={active ? 'text-accent' : 'text-fg-dim'}>{it.icon}</span>
                      <span className="flex-1 text-left truncate">{it.label}</span>
                      {it.hint && <span className="text-[11px] text-fg-dim shrink-0 truncate max-w-[40%]">{it.hint}</span>}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="px-4 h-9 border-t border-bg-border bg-bg-raised/40 flex items-center justify-between text-[11px] text-fg-dim">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1"><span className="kbd">↑</span><span className="kbd">↓</span> navigate</span>
            <span className="flex items-center gap-1"><span className="kbd">↵</span> open</span>
          </div>
          <span>{filtered.length} result{filtered.length === 1 ? '' : 's'}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Scoring: prefix > word-boundary > substring > all-chars-in-order.
 * Light enough to run on every keystroke for ~50 items.
 */
function score(query: string, target: string): number {
  if (!query) return 1;
  if (target.startsWith(query)) return 100;
  // word-boundary match (after space, underscore, or digit)
  const wbMatch = new RegExp(`(^|[\\s_#])${escapeRx(query)}`).test(target);
  if (wbMatch) return 60;
  if (target.includes(query)) return 40;
  // all chars in order
  let ti = 0;
  for (const c of query) {
    const found = target.indexOf(c, ti);
    if (found < 0) return 0;
    ti = found + 1;
  }
  return 10;
}

function escapeRx(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
